import { injectScript } from '../../utils/script';
import { getBrand } from '@jetshop/core/components/OpenGraph/OpenGraphProductData';
import { unSlugify } from '../unslugify';
import { breadcrumbsWithoutProduct } from '../../utils/breadcrumbsWithoutProduct';
import { CONSENT_COOKIE_NAME, defaultConsentSettings } from '../../AnalyticsProvider';
function push(event) {
    if (window.dataLayer) {
        if (event.ecommerce) {
            window.dataLayer.push({ ecommerce: null });
        }
        window.dataLayer.push(event);
    }
}
function getProductData({ product, selectedVariation, useArticleNumberAsId = false, brandCustomField, category = '' }) {
    if (useArticleNumberAsId) {
        return {
            id: (selectedVariation === null || selectedVariation === void 0 ? void 0 : selectedVariation.articleNumber) || product.articleNumber,
            productId: product.id,
            productSKU: product.articleNumber,
            variant: selectedVariation === null || selectedVariation === void 0 ? void 0 : selectedVariation.values.join('_'),
            variantSKU: selectedVariation === null || selectedVariation === void 0 ? void 0 : selectedVariation.articleNumber,
            name: product.name,
            base_product: product.articleNumber,
            category,
            brand: getBrand(product, brandCustomField)
        };
    }
    else {
        // This is kept as backwards compatibility. Some shops rely on the product id being the internal Jetshop id, instead of the article number.
        // Changing this could affect the statistics in Google Ads/Merchant/Analytics, so we want the new behaviour to be opt-in.
        return {
            id: product.id,
            variant: selectedVariation
                ? selectedVariation.articleNumber
                : product.articleNumber,
            name: product.name,
            base_product: product.articleNumber,
            category,
            brand: getBrand(product, brandCustomField)
        };
    }
}
export const gtmTracker = ({ containerId, currency, useArticleNumberAsId = false, brandCustomField }) => ({
    name: 'gtm',
    initBrowser({ config }) {
        const script = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
        const w = window;
        w.dataLayer = w.dataLayer || [];
        w.dataLayer.push({
            'gtm.start': new Date().getTime(),
            event: 'gtm.js',
            originalLocation: document.location.protocol +
                '//' +
                document.location.hostname +
                document.location.pathname +
                document.location.search
        });
        if (config.useTrackingConsentAPI) {
            const consents = config.cookies.get(CONSENT_COOKIE_NAME) || defaultConsentSettings;
            // NB: Only cookieless pings will be sent to Google servers.
            // There’s also a wait time of 500 milliseconds before any Google tags are allowed to fire,
            // to give time for the update command to be invoked.
            w.dataLayer.push(Object.assign({ event: 'gtm_consent_default' }, consents));
        }
        injectScript({ src: script });
    },
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    consent() {
        // Consent updated should be handled from inside GTM
        // It should be just a matter of dataLayer.push({ event: 'gtm_consent_update', ...consents })).
        // One can have a look in Simo Ahava GTM template for an example.
    },
    trackProduct({ quantity, action, product, selectedVariation, list, listName, index, category }, context, eventId) {
        const productData = Object.assign(Object.assign({}, getProductData({
            product,
            selectedVariation,
            useArticleNumberAsId,
            brandCustomField,
            category
        })), { price: (selectedVariation && selectedVariation.price.incVat) ||
                (product.price && product.price.incVat), quantity, position: index });
        switch (action) {
            case 'click':
                push({
                    event: 'productClick',
                    productIds: product.articleNumber,
                    ecommerce: {
                        click: {
                            actionField: { list: list || listName || context.list },
                            products: [productData]
                        }
                    },
                    eventId
                });
                break;
            case 'view':
                push({
                    event: 'productDetails',
                    ecommerce: {
                        currencyCode: currency,
                        detail: {
                            products: [productData]
                        }
                    },
                    eventId
                });
                break;
            case 'add':
                push({
                    event: 'addToCart',
                    productIds: product.articleNumber,
                    ecommerce: {
                        actionField: {
                            list: list || listName || context.list
                        },
                        currencyCode: currency,
                        add: {
                            products: [productData]
                        }
                    },
                    eventId
                });
                break;
            case 'remove':
                push({
                    event: 'removeFromCart',
                    productIds: product.articleNumber,
                    pageType: 'cart',
                    ecommerce: {
                        currencyCode: currency,
                        remove: {
                            products: [productData]
                        }
                    },
                    eventId
                });
                break;
            default:
                console.log('Error: Gtag can not track action ' + action);
                break;
        }
    },
    trackCartCheckout({ cart }, _, eventId) {
        const items = cart.items.map((item) => {
            var _a, _b, _c;
            return (Object.assign(Object.assign({}, getProductData({
                product: item.product,
                selectedVariation: item.variant,
                useArticleNumberAsId,
                brandCustomField,
                category: breadcrumbsWithoutProduct((_a = item.product.primaryRoute) === null || _a === void 0 ? void 0 : _a.breadcrumbs) ||
                    unSlugify((_c = (_b = item.product.canonicalCategory) === null || _b === void 0 ? void 0 : _b.primaryRoute) === null || _c === void 0 ? void 0 : _c.path) ||
                    ''
            })), { price: item.total.incVat, quantity: item.quantity }));
        });
        push({
            event: 'checkout',
            ecommerce: {
                products: items
            },
            eventId
        });
    },
    trackPageView({ pathname, title }, _, eventId) {
        push({
            event: 'pageView',
            pagePath: pathname,
            pageTitle: title,
            eventId
        });
    },
    trackList({ listName, products, category }, _, eventId) {
        const productData = products.map((product, i) => {
            var _a, _b, _c, _d;
            return (Object.assign(Object.assign({}, getProductData({
                product,
                selectedVariation: null,
                useArticleNumberAsId,
                brandCustomField,
                category: category ||
                    breadcrumbsWithoutProduct((_a = product === null || product === void 0 ? void 0 : product.primaryRoute) === null || _a === void 0 ? void 0 : _a.breadcrumbs) ||
                    unSlugify((_c = (_b = product === null || product === void 0 ? void 0 : product.canonicalCategory) === null || _b === void 0 ? void 0 : _b.primaryRoute) === null || _c === void 0 ? void 0 : _c.path)
            })), { price: (_d = product.price) === null || _d === void 0 ? void 0 : _d.incVat, position: i, list: listName }));
        });
        push({
            event: 'productList',
            ecommerce: {
                currencyCode: currency,
                impressions: productData
            },
            eventId
        });
    },
    trackExperiment({ id, variant }, _, eventId) {
        push({
            experiment: `${id}.${variant}`,
            eventId
        });
    },
    trackUser({ userId }, _, eventId) {
        push({
            userId,
            eventId
        });
    },
    trackLogin(_, __, eventId) {
        push({
            event: 'login',
            eventId
        });
    },
    trackRegister(_, __, eventId) {
        push({
            event: 'register',
            eventId
        });
    },
    trackAddToWishList({ articleNumber, productName }, _, eventId) {
        push({
            event: 'addToWishlist',
            articleNumber,
            productName,
            eventId
        });
    }
});
//# sourceMappingURL=gtm.js.map